<template>
  <!--Breadcrumb-->
  <div class="container-fluid" v-if="vendor">
    <div class="row">
      <div class="col-8 offset-2">
        <nav style="font-size: 35px" aria-label="breadcrumb">
          <ol class="breadcrumb py-0 pt-3 container bg-light">
            <li class="breadcrumb-item pl-2 pr-2">
              <router-link to="/dn/v2">Wareneingang</router-link>
            </li>
            <li class="breadcrumb-item pl-2 pr-2">
              <router-link :to="'/dn/v2/vendor/' + vendor.id">{{
                vendor.name
              }}</router-link>
            </li>
            <li class="breadcrumb-item pl-2 pr-2">
              <a aria-current="page">Reklamationen</a>
            </li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- Filter Fenster-->
    <div class="row">
      <div class="col-2">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Filter</h5>
            <div class="input-group rounded pb-3">
              <input
                type="search"
                class="form-control rounded"
                placeholder="z.B EAN"
                aria-label="Search"
                aria-describedby="search-addon"
                v-model="filter"
              />
            </div>
            <!--Checkbox Filter-->
            <div class="mb-3">
              <b-form-checkbox
                v-model="openOnly"
                :value="true"
                :unchecked-value="false"
                :disabled="processedOnly"
              >
                Nur offene
              </b-form-checkbox>
              <b-form-checkbox
                v-model="processedOnly"
                :value="true"
                :unchecked-value="false"
                :disabled="openOnly"
              >
                Nur reklamierte
              </b-form-checkbox>
            </div>
            <div>
              <b-button
                variant="primary"
                class="btn btn-primary btn-block"
                @click="exportClaimsToXLSX(filteredClaims)"
                ><i class="bi bi-file-earmark-spreadsheet mr-2"></i>Export to
                Excel</b-button
              >
              <b-button
                variant="primary"
                class="btn btn-primary btn-block"
                @click="exportToPDF()"
                ><i class="bi bi-file-earmark-pdf mr-2"></i> Export to
                PDF</b-button
              >
              <b-button
                variant="primary"
                class="btn btn-primary btn-block"
                @click="processClaims(selectedClaims)"
                :disabled="
                  selectedClaims.length == 0 || !allSelectedClaimsUnprocessed
                "
              >
                {{ selectedClaims.length }} x Reklamieren
              </b-button>

              <b-button
                variant="primary"
                class="btn btn-danger btn-block"
                @click="completeClaims(selectedClaims)"
                :disabled="
                  selectedClaims.length == 0 || !allSelectedClaimsProcessed
                "
              >
                {{ selectedClaims.length }} x Abschließen</b-button
              >
            </div>
          </div>
        </div>
      </div>
      <div class="col-8">
        <div class="card">
          <b-overlay :show="showOverlay" rounded="sm">
            <div class="card-body">
              <div>
                <div v-if="vendor">
                  <div class="text-center" ref="pageToPrint" id="pageToPrint">
                    <b-table
                      striped
                      hover
                      class="table"
                      :items="filteredClaims"
                      :fields="fields"
                      size="sm"
                      style="page-break-after: always"
                      sort-by="createdAt"
                      :sort-desc="true"
                    >
                      <template #head(_isSelected)>
                        <b-form-checkbox
                          :checked="allSelected"
                          :value="true"
                          :unchecked-value="false"
                          @change="processCheckAllSelections"
                        >
                        </b-form-checkbox>
                      </template>

                      <template #cell(_isSelected)="data">
                        <b-form-checkbox
                          v-model="data.item._isSelected"
                          @click="processCheckAllSelections"
                          :value="true"
                          :unchecked-value="false"
                        >
                        </b-form-checkbox>
                      </template>

                      <template #cell(createdAt)="celldata">
                        {{
                          moment(celldata.item.createdAt).format(
                            "DD.MM.YYYY HH:mm",
                          )
                        }}
                        {{ getUserShortname(celldata.item.creatorUserId) }}
                      </template>

                      <template #cell(processedAtAndBy)="celldata">
                        <div
                          :class="{
                            'text-danger': celldata.item.processedAt == null,
                          }"
                        >
                          {{
                            celldata.item.processedAt
                              ? moment(celldata.item.processedAt).format(
                                  "DD.MM.YYYY HH:mm",
                                )
                              : "Nicht reklamiert"
                          }}
                          {{ getUserShortname(celldata.item.processorUserId) }}
                        </div>
                      </template>

                      <template #cell(type)="data">
                        {{ typeFormatter(data.value) }}
                      </template>
                      <template #cell(creatorUserId)="data">
                        {{ creatorUserIdFormatter(data.value) }}
                      </template>
                      <template #cell(c)="data">
                        {{ moment(data.value).format("HH:mm DD.MM.YYYY") }}
                      </template>
                      <template #cell(comment)="data">
                        <click-to-edit-component
                          v-model="data.item.comment"
                          :dataId="data.item.id"
                          dataPath="/deliverynotepositionclaims/comment"
                        ></click-to-edit-component>
                      </template>
                      <template #cell(details)="data">
                        <click-to-edit-component
                          v-model="data.item.details"
                          :dataId="data.item.id"
                          dataPath="/deliverynotepositionclaims/details"
                        ></click-to-edit-component>
                      </template>
                    </b-table>
                  </div>
                </div>
                <div v-else>
                  <p>Wird geladen.</p>
                </div>
                <modal-error-component
                  v-model="modalError"
                ></modal-error-component>
                <b-button
                  v-if="devModeOn"
                  variant="danger"
                  @click="resetClaims()"
                  >reset Claims</b-button
                >
              </div>
            </div>
          </b-overlay>
        </div>
      </div>
      <div class="col-2" v-if="vendor">
        <div class="card">
          <div class="card-body">
            <div v-if="!infoEditorActive">
              <p
                @click="openInfoEditor"
                v-html="vendor.claimInfo ? vendor.claimInfo : 'Info hinzufügen'"
              ></p>
            </div>
            <div v-else>
              <ckeditor
                :editor="infoEditor"
                v-model="infoEditorData"
                :config="infoEditorConfig"
                @ready="onReady"
                class="my-2 border"
              ></ckeditor>
              <button class="btn btn-primary mr-1" @click="commitInfoEditor">
                <i class="bi bi-check " style="font-size: 1.25em"></i>
              </button>
              <button class="btn btn-primary" @click="cancelInfoEditor">
                <i class="bi bi-x" style="font-size: 1.25em"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import DecoupledEditor from "@ckeditor/ckeditor5-build-decoupled-document";

export default {
  data() {
    return {
      vendor: null,
      filter: "",
      claims: [],
      formFields: [],
      isSortable: true,
      fields: [
        { key: "_isSelected" },
        { key: "deliverynoteposition.barcode", label: "barcode" },
        { key: "quantity", label: "quantity" },
        { key: "type", label: "reason" },
        { key: "details", label: "details" },
        {
          key: "deliverynoteposition.deliverynote.number",
          label: "invoice number",
        },
        { key: "createdAt", label: "created at/by", sortable: true },
        { key: "processedAtAndBy", label: "claimed at/by" },
        { key: "comment", label: "comment" },
      ],
      modalError: {
        show: false,
        message: "unpopulated",
        error: "unpopulated",
        title: "unpopulated",
      },
      showOverlay: false,
      processedOnly: false,
      openOnly: false,
      users: [],
      userId: 0,
      //resetClaims Button will be rendered if true
      devModeOn: false,
      infoEditor: DecoupledEditor,
      infoEditorData: "",
      infoEditorConfig: {
        toolbar: [
          "heading",
          "|",
          "bold",
          "italic",
          "underline",
          "alignment",
          "|",
          "fontColor",
          "fontBackgroundColor",
        ],
      },
      infoEditorActive: false,
    };
  },
  created() {
    Promise.all([
      axios.get("/user/all"),
      axios.get("/user/self"),
      axios.get("/vendor/" + this.$route.params.vendorId + "/fetch"),
      axios.get(
        "/deliverynotepositionclaims/vendor/" + this.$route.params.vendorId,
      ),
    ]).then((responses) => {
      this.users = responses[0].data;
      this.userId = responses[1].data.id;
      this.vendor = responses[2].data;
      this.claims = responses[3].data.map((claim) => {
        claim._isSelected = false;
        return claim;
      });
    });
  },
  computed: {
    filteredClaims() {
      return this.claims.filter(
        (claim) =>
          claim.deliverynoteposition.barcode.includes(this.filter.trim()) &&
          (!this.openOnly || claim.processedAt == null) &&
          (!this.processedOnly || claim.processedAt != null),
      );
    },
    selectedClaims() {
      return this.filteredClaims.filter((claim) => claim._isSelected);
    },
    processedClaims() {
      return this.filteredClaims.filter((claim) => claim.processedAt != null);
    },
    allSelected() {
      return this.filteredClaims.length == this.selectedClaims.length;
    },
    allSelectedClaimsProcessed() {
      return this.selectedClaims
        .map((claim) => claim.processedAt != null)
        .reduce((acc, cur) => acc && cur, true);
    },
    allSelectedClaimsUnprocessed() {
      return this.selectedClaims
        .map((claim) => claim.processedAt == null)
        .reduce((acc, cur) => acc && cur, true);
    },
    allProcessed() {
      return this.claims.length == this.processedClaims.length;
    },
  },
  methods: {
    openInfoEditor() {
      this.infoEditorData = this.vendor.claimInfo ? this.vendor.claimInfo : "";
      this.infoEditorActive = true;
    },
    commitInfoEditor() {
      let payload = {
        vendorId: this.vendor.id,
        claimInfo: this.infoEditorData,
      };
      console.log(payload);
      axios
        .put("/vendor/update/claimInfo", payload)
        .then((response) => {
          this.vendor.claimInfo = this.infoEditorData;
          this.infoEditorActive = false;
        })
        .catch((error) => {
          this.modalError = {
            show: true,
            message: "ClaimInfo speichern fehlgeschalgen.",
            error: error,
            title: "ClaimInfo speichern",
          };
        });
      //TODO: Commit to server
    },
    uncheckAllclaims() {
      this.claims = this.claims.map((claim) => {
        claim._isSelected = false;
        return claim;
      });
    },
    processCheckAllSelections() {
      var inv = !this.allSelected;
      this.filteredClaims.forEach((claim) => {
        claim._isSelected = inv;
      });
      /*
            <=> Equivalent
            if(this.allSelected)
            {
                this.filteredDelieryNotes.forEach(dn => dn._isSelected = false )
            }
            else
            {
                this.filteredDelieryNotes.forEach(dn => dn._isSelected = true )
            }*/
    },
    cancelInfoEditor() {
      this.infoEditorData = null;
      this.infoEditorActive = false;
    },
    onReady(editor) {
      // Insert the toolbar before the editable area.
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement(),
        );
    },
    typeFormatter(type) {
      switch (type) {
        case 1:
          return "damaged";
        case 2:
          return "missing";
        case 3:
          return "misspick";
        case 4:
          return "wrong price";
        default:
          return "Das sollte nicht passieren.";
      }
    },
    creatorUserIdFormatter(creatorUserId) {
      if (!creatorUserId) return "-";
      let acronym = this.users.find((user) => user.id == creatorUserId);
      return acronym ? acronym.shortname : "-";
    },
    /*
        ============== UNUSED ==============
        exportToXLSX() {
            let currentDate = moment().format("DD.MM.YYYY");
            this.toggleSortable();
            //with "nextTick" the export is only created when the sortable arraws are not rendered 
            //otherwise sortable arrows will be transformed to text "Click here to sort" in the header fields
            Vue.nextTick().then(() => {
                const wb = XLSX.utils.table_to_book(this.$el.querySelector('#pageToPrint'), { sheet: this.vendor.name });
                XLSX.writeFile(wb, this.vendor.name + "-Lieferscheinreklamation-" + currentDate + ".xlsx");
                this.toggleSortable();
            })
        },
        ============== UNUSED ==============
        */
    exportClaimsToXLSX(claims) {
      let currentDate = moment().format("DD.MM.YYYY");
      //this.toggleSortable();
      let mappedClaims = claims.map((claim) => {
        return {
          barcode: claim.deliverynoteposition.barcode,
          quantity: claim.quantity,
          reason: this.typeFormatter(claim.type),
          details: claim.details,
          "invoice number": claim.deliverynoteposition.deliverynote.number,
          "createad at": moment(claim.createdAt).format("DD.MM.YYYY HH:mm"),
          "claimed at": claim.processedAt
            ? moment(claim.processedAt).format("DD.MM.YYYY HH:mm")
            : "Nicht reklamiert",
          comment: claim.comment,
        };
      });
      //with "nextTick" the export is only created when the sortable arraws are not rendered
      //otherwise sortable arrows will be transformed to text "Click here to sort" in the header fields
      Vue.nextTick().then(() => {
        const ws = XLSX.utils.json_to_sheet(mappedClaims, {
          sheet: this.vendor.name,
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reklamationen");
        XLSX.writeFile(
          wb,
          this.vendor.name +
            "-Lieferscheinreklamation-" +
            currentDate +
            ".xlsx",
        );
        //this.toggleSortable();
      });
    },
    //
    exportToPDF() {
      let currentDate = moment().format("DD.MM.YYYY");
      this.toggleSortable();
      Vue.nextTick().then(() => {
        let options = {
          margin: 2,
          filename:
            this.vendor.name +
            "-Lieferscheinreklamation-" +
            currentDate +
            ".pdf",
          pagebreak: { mode: "avoid-all", before: ".tr" },
          //landscape - horizontal //portrait - vertical - default
          jsPDF: { orientation: "landscape" },
        };
        const element = this.$refs.pageToPrint;
        html2pdf(element, options);
        this.toggleSortable();
      });
      // documentation for html2pdf:
      //https://ekoopmans.github.io/html2pdf.js/
    },
    completeClaims(claims) {
      this.showOverlay = true;
      Vue.nextTick(() => {
        Promise.all(claims.map((c) => this.completelaim(c))).then(() => {
          this.showOverlay = false;
          this.exportClaimsToXLSX(claims);
          this.removeCompletedClaims();
        });
        this.uncheckAllclaims();
      });
    },
    completelaim(claim) {
      return new Promise((resolve, reject) => {
        axios
          .put("/deliverynotepositionclaims/complete/" + claim.id)
          .then((response) => {
            if (response.status >= 200 && response.status <= 226) {
              console.log(response.data);
              claim.completedAt = response.data.completedAt;
              resolve();
            } else {
              console.log(response);
              let error = "status: " + response.status + "\n";
              error += "response: \n" + JSON.stringify(response, null, 2);
              reject(error);
              this.modalError = {
                show: true,
                message: "Claim konnte nicht abgeschlossen werden.",
                error: error,
                title: "Complete Claim",
              };
            }
          })
          .catch((err) => {
            console.log(err);
            this.modalError = {
              show: true,
              message: "Claim konnte nicht abgeschlossen werden.",
              error: err,
              title: "Complete Claim",
            };
            reject(err);
          });
      });
    },
    removeCompletedClaims() {
      this.claims = this.claims.filter((claim) => claim.completedAt == null);
    },
    toggleSortable() {
      this.isSortable = !this.isSortable;
      this.fields.forEach((field) => {
        field.sortable = this.isSortable;
      });
    },
    getUserShortname(userId) {
      var user = this.users.find((user) => user.id == userId);
      if (!user) return "";
      return user.shortname;
    },
    processClaims(claims) {
      this.showOverlay = true;
      Vue.nextTick(() => {
        Promise.all(claims.map((c) => this.processClaim(c))).then(() => {
          this.showOverlay = false;
        });
      });
    },
    processClaim(claim) {
      return new Promise((resolve, reject) => {
        axios
          .put("/deliverynotepositionclaims/process/" + claim.id)
          .then((response) => {
            if (response.status >= 200 && response.status <= 226) {
              this.uncheckAllclaims();
              console.log(response.data);
              claim.processedAt = response.data.processedAt;
              claim.processorUserId = response.data.processorUserId;
              resolve();
            } else {
              console.log(response);
              let error = "status: " + response.status + "\n";
              error += "response: \n" + JSON.stringify(response, null, 2);
              reject(error);
              this.modalError = {
                show: true,
                message: "Verarbeitungsgrund konnte nicht gesetzt werden.",
                error: error,
                title: "Set Processing Reason",
              };
            }
          })
          .catch((err) => {
            console.log(err);
            this.modalError = {
              show: true,
              message: "Verarbeitungsgrund konnte nicht gesetzt werden.",
              error: err,
              title: "Set Processing Reason",
            };
            reject(err);
          });
      });
    },
  },
};
</script>
<style scoped>
.table td {
  padding: 0;
}
</style>
